/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { chatSummarizeToolCall } from "../funcs/chatSummarizeToolCall.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SummarizeToolCallResult } from "../models/components/summarizetoolcallresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SummarizeToolCallRequest,
  SummarizeToolCallSecurity,
} from "../models/operations/summarizetoolcall.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SummarizeToolCallMutationVariables = {
  request: SummarizeToolCallRequest;
  security?: SummarizeToolCallSecurity | undefined;
  options?: RequestOptions;
};

export type SummarizeToolCallMutationData = SummarizeToolCallResult;

export type SummarizeToolCallMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * summarizeToolCall chat
 *
 * @remarks
 * Generate or return a persisted two-sentence summary of one tool call. Concurrent requests share the same cached result.
 */
export function useSummarizeToolCallMutation(
  options?: MutationHookOptions<
    SummarizeToolCallMutationData,
    SummarizeToolCallMutationError,
    SummarizeToolCallMutationVariables
  >,
): UseMutationResult<
  SummarizeToolCallMutationData,
  SummarizeToolCallMutationError,
  SummarizeToolCallMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSummarizeToolCallMutation(client, options),
    ...options,
  });
}

export function mutationKeySummarizeToolCall(): MutationKey {
  return ["@gram/client", "chat", "summarizeToolCall"];
}

export function buildSummarizeToolCallMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SummarizeToolCallMutationVariables,
  ) => Promise<SummarizeToolCallMutationData>;
} {
  return {
    mutationKey: mutationKeySummarizeToolCall(),
    mutationFn: function summarizeToolCallMutationFn({
      request,
      security,
      options,
    }): Promise<SummarizeToolCallMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(chatSummarizeToolCall(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
